import { Bell, Languages, LogOut, Menu, MoonStar, SunMedium, Volume2, VolumeX, X } from "lucide-react";
import { setSoundEnabled, useSoundEnabled } from "../../lib/activity-alerts";
import { useState, type ReactNode } from "react";
import { NavLink } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/DropdownMenu";
import type { ProviderVenue } from "../../lib/api";
import type { AppLanguage, ThemePreference, TranslationFunction } from "../../lib/preferences";
import {
  sectionCompactLabelKeys,
  sectionIcons,
  sectionLabelKeys,
  roleLabel,
  VenueSwitcher,
  type ProviderSection,
} from "./provider-workspace-helpers";
import { NavBadgeChip, type NavBadge } from "./NavBadge";

/** The day-to-day screens get a thumb-reachable tab bar; everything else sits under More. */
export const mobileTabSections: ProviderSection[] = ["dashboard", "floor", "orders", "schedule", "register"];
export const mobileMoreSections: ProviderSection[] = ["customers", "tournaments", "menu", "venue-setup", "business"];

interface MobileHeaderProps {
  activeSection: ProviderSection;
  language: AppLanguage;
  nextLanguage: AppLanguage;
  nextThemePreference: ThemePreference;
  notificationsPanel?: ReactNode;
  onSelectVenue: (venueId: string) => void;
  onSetLanguage: (language: AppLanguage) => void;
  onSetThemePreference: (theme: ThemePreference) => void;
  onSignOut: () => void;
  pendingCount?: number;
  providerName: string;
  selectedVenueId: string | null;
  sectionBadges?: Partial<Record<ProviderSection, NavBadge>>;
  t: TranslationFunction;
  themePreference: ThemePreference;
  unreadNotificationCount?: number;
  userRole?: string;
  venues: ProviderVenue[];
}

export function MobileHeader({
  activeSection,
  language,
  nextLanguage,
  nextThemePreference,
  notificationsPanel,
  onSelectVenue,
  onSetLanguage,
  onSetThemePreference,
  onSignOut,
  pendingCount = 0,
  providerName,
  selectedVenueId,
  sectionBadges = {},
  t,
  themePreference,
  unreadNotificationCount = 0,
  userRole,
  venues,
}: MobileHeaderProps) {
  const ThemeToggleIcon = themePreference === "dark" ? SunMedium : MoonStar;
  const languageLabel = language === "ar" ? t("preferences.language.en") : t("preferences.language.ar");
  const themeLabel = nextThemePreference === "light" ? t("preferences.theme.light") : t("preferences.theme.dark");
  const [menuOpen, setMenuOpen] = useState(false);
  const soundEnabled = useSoundEnabled();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  return (
    <header className="provider-mobile-header grid gap-2 lg:hidden">
      {menuOpen ? (
        <div aria-hidden="true" className="provider-mobile-menu-backdrop" />
      ) : null}
      <div className="ops-surface ops-surface-dense">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2">
            <span className="provider-brand-mark shrink-0" title={providerName}>
              {providerName.slice(0, 2).toUpperCase()}
            </span>
            <span className="provider-mobile-provider-name hidden truncate text-base font-semibold text-white sm:block" title={providerName}>
              {providerName}
            </span>
            <div className="min-w-0">
              <VenueSwitcher
                onSelect={onSelectVenue}
                selectedVenueId={selectedVenueId}
                venues={venues}
              />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            {notificationsPanel ? (
              <button
                aria-expanded={notificationsOpen}
                aria-label={t("Notifications")}
                className="ops-button provider-notifications-bell-button"
                data-size="sm"
                data-variant={notificationsOpen ? "primary" : "secondary"}
                onClick={() => setNotificationsOpen((current) => !current)}
                title={t("Notifications")}
                type="button"
              >
                <Bell size={16} strokeWidth={2.2} />
                {unreadNotificationCount > 0 ? (
                  <span className="provider-notifications-bell-badge">
                    {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
                  </span>
                ) : null}
              </button>
            ) : null}
            <button
              aria-label={languageLabel}
              className="ops-button"
              data-size="sm"
              data-variant="secondary"
              onClick={() => onSetLanguage(nextLanguage)}
              title={languageLabel}
              type="button"
            >
              <Languages size={16} strokeWidth={2.2} />
            </button>
            <button
              aria-label={themeLabel}
              className="ops-button"
              data-size="sm"
              data-variant="secondary"
              onClick={() => onSetThemePreference(nextThemePreference)}
              title={themeLabel}
              type="button"
            >
              <ThemeToggleIcon size={16} strokeWidth={2.2} />
            </button>

            <DropdownMenu onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label={t("More")}
                  className="ops-button"
                  data-size="sm"
                  data-variant="secondary"
                  title={t("More")}
                  type="button"
                >
                  <Menu size={16} strokeWidth={2.2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="provider-mobile-menu w-56">
                <DropdownMenuLabel>{providerName}</DropdownMenuLabel>
                {userRole ? (
                  <DropdownMenuItem className="gap-2" disabled>
                    <span className="text-xs text-[var(--color-muted)]">{roleLabel(userRole, t)}</span>
                  </DropdownMenuItem>
                ) : null}
                {mobileMoreSections.map((section) => {
                  const Icon = sectionIcons[section];
                  return (
                    <DropdownMenuItem asChild className="min-h-11 gap-2" key={section}>
                      <NavLink
                        aria-current={activeSection === section ? "page" : undefined}
                        aria-label={t(sectionLabelKeys[section])}
                        to={`/provider/${section}`}
                      >
                        {Icon ? <Icon size={16} strokeWidth={2.2} /> : null}
                        {t(sectionLabelKeys[section])}
                      </NavLink>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuItem className="min-h-11 gap-2" onSelect={() => setSoundEnabled(!soundEnabled)}>
                  {soundEnabled ? <Volume2 size={16} strokeWidth={2.2} /> : <VolumeX size={16} strokeWidth={2.2} />}
                  {soundEnabled ? t("Sound on for new orders") : t("Sound off for new orders")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  aria-label={t("common.signOut")}
                  className="min-h-11 gap-2 text-[var(--color-error)] focus:bg-[var(--color-error-m)] focus:text-[var(--color-error)]"
                  onSelect={() => void onSignOut()}
                >
                  <LogOut size={16} strokeWidth={2.2} />
                  {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {notificationsOpen && notificationsPanel ? (
        <div
          className="provider-mobile-notifications pn-sheet"
          onClick={(event) => {
            // Bubble after the entry handler runs; capture can unmount its button
            // before the browser dispatches the target click. Collapse after opening
            // so the deep-linked section is immediately visible.
            const target = event.target as HTMLElement | null;
            if (
              target?.closest(
                '[data-testid="provider-notification-item"], [data-testid="provider-notification-pending"] .ops-button',
              )
            ) {
              setNotificationsOpen(false);
            }
          }}
        >
          <button
            aria-label={t("Close notifications")}
            className="pn-drawer-close"
            onClick={() => setNotificationsOpen(false)}
            type="button"
          >
            <X aria-hidden="true" size={18} />
          </button>
          {notificationsPanel}
        </div>
      ) : null}

      <nav aria-label={t("provider.sectionsMenu")} className="provider-mobile-tabbar">
        {mobileTabSections.map((section) => {
          const Icon = sectionIcons[section];
          const badge = section === "schedule" && !sectionBadges.schedule && pendingCount > 0
            ? { count: pendingCount, tone: "attention" as const, label: t("{count} pending", { count: pendingCount }) }
            : sectionBadges[section];
          return (
            <NavLink
              aria-label={t(sectionLabelKeys[section])}
              className="provider-mobile-tab"
              data-active={activeSection === section ? "true" : undefined}
              key={section}
              to={`/provider/${section}`}
            >
              <span className="provider-mobile-tab-icon">
                {Icon ? <Icon aria-hidden="true" size={20} strokeWidth={2.1} /> : null}
                <NavBadgeChip badge={badge} />
              </span>
              <span aria-hidden="true" className="provider-mobile-tab-label">{t(sectionCompactLabelKeys[section])}</span>
            </NavLink>
          );
        })}
      </nav>
    </header>
  );
}
